import React, { useMemo, useState, useEffect } from "react";

/**
 * Client-side pagination for arrays of records. Returns the current page slice
 * plus a render-ready footer component.
 *
 * Convention per user request (Iter15 #5): max 50 items per page. Newest rows
 * (assumed first in the array) populate page 1; overflow flows to page 2+.
 *
 * Usage:
 *   const { pageItems, Pagination } = usePagination(rows, { perPage: 50 });
 *   return (<>{pageItems.map(...)}{Pagination}</>);
 */
export function usePagination(items, { perPage = 50 } = {}) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / perPage));
  // Clamp page if the data shrank (e.g. after a filter or delete)
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * perPage;
    return (items || []).slice(start, start + perPage);
  }, [items, page, perPage]);

  const Pagination = (items?.length || 0) <= perPage ? null : (
    <div className="flex items-center justify-between gap-3 text-xs text-stone-600 mt-3 p-3 bg-stone-50 border border-stone-200">
      <div className="font-mono">
        Menampilkan <b>{(page - 1) * perPage + 1}</b>–<b>{Math.min(page * perPage, items.length)}</b> dari <b>{items.length}</b>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(1)}
          disabled={page === 1}
          className="px-2 py-1 border border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pg-first"
        >«</button>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="px-2 py-1 border border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pg-prev"
        >‹</button>
        <span className="px-2 font-mono">Hal {page} / {totalPages}</span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className="px-2 py-1 border border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pg-next"
        >›</button>
        <button
          onClick={() => setPage(totalPages)}
          disabled={page === totalPages}
          className="px-2 py-1 border border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pg-last"
        >»</button>
      </div>
    </div>
  );

  return { pageItems, Pagination, page, setPage, totalPages };
}
